import React, { useEffect, useState } from "react";
import { GitMerge, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { dashApi } from "../api";

/**
 * Admin-only dialog: merge ``source`` company into a chosen target.
 * Moves every submission and tombstones the source. Irreversible.
 *
 * Props:
 *   open
 *   onClose
 *   source     — the company doc currently being viewed
 *   onMerged   — (result) => void  — caller usually navigates to target slug
 */
export default function MergeCompanyDialog({ open, onClose, source, onMerged }) {
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [targetId, setTargetId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTargetId(""); setQ("");
    dashApi.get("/companies", { params: { limit: 200 } })
      .then(({ data }) => setCompanies(data.items || []))
      .catch(() => setCompanies([]));
  }, [open]);

  const filtered = companies
    .filter((c) => c.id && c.id !== source?.id)
    .filter((c) => !q.trim() || (c.company || "").toLowerCase().includes(q.trim().toLowerCase()));

  const handleMerge = async () => {
    if (!targetId) return;
    setBusy(true);
    try {
      const { data } = await dashApi.post(
        `/companies/${source.id}/merge`,
        { target_id: targetId }
      );
      toast.success(`Merged into ${data.target_name} (${data.moved_submissions} submission${data.moved_submissions === 1 ? "" : "s"} moved).`);
      onMerged?.(data);
      onClose();
    } catch (e) {
      toast.error(e.message || "Merge failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="merge-company-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-4 w-4" /> Merge company
          </DialogTitle>
          <DialogDescription>
            Move every submission from <strong>{source?.company}</strong> into another
            company. This action <strong>cannot be undone</strong> — pick carefully.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2 mb-2">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div>
            Auto-linking uses exact-match-with-suffix-strip. Use this dialog to
            consolidate accidental duplicates (e.g., "vG inc." vs "Van Gelder, Inc.").
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="merge-search" className="text-xs">Find target company</Label>
            <Input
              id="merge-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type to filter…"
              disabled={busy}
              data-testid="merge-search-input"
            />
          </div>

          <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <div className="p-3 text-xs text-slate-500 italic">No other companies match.</div>
            ) : (
              filtered.slice(0, 50).map((c) => (
                <label
                  key={c.id}
                  className={`flex items-start gap-2 p-2.5 cursor-pointer transition-colors ${
                    targetId === c.id ? "bg-blue-50" : "hover:bg-slate-50"
                  }`}
                  data-testid={`merge-target-${c.key}`}
                >
                  <input
                    type="radio"
                    name="merge-target"
                    value={c.id}
                    checked={targetId === c.id}
                    onChange={() => setTargetId(c.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.company}</div>
                    <div className="text-xs text-slate-500">
                      {c.submission_count} sub{c.submission_count === 1 ? "" : "s"}
                      {c.primary_email ? ` • ${c.primary_email}` : ""}
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleMerge}
            disabled={busy || !targetId}
            data-testid="merge-confirm-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <GitMerge className="h-3.5 w-3.5 mr-1.5" />}
            Merge company
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
